import { ChevronDownIcon } from '@radix-ui/react-icons'
import { animated, useSpring } from '@react-spring/web'
import clsx from 'clsx'
import React, { useState } from 'react'

export type InstructionsProps = {
  logoUrl: string;
  instructionalImages: { [key: string]: string };
}

const AnimatedAccordionItem = ({
  header,
  children,
  isLastItem,
}: {
  header: string;
  children: React.ReactNode;
  isLastItem?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const contentStyles = useSpring({
    from: { maxHeight: 0, opacity: 0 },
    to: {
      maxHeight: isOpen ? 20000 : 0,
      opacity: isOpen ? 1 : 0,
    },
  })

  return (
    <div className={clsx('px-8 py-2', { 'border-b': !isLastItem })}>
      <div className={clsx('overflow-hidden')}>
        <div className='flex'>
          <div
            className={clsx(
              'flex h-20 flex-1 cursor-pointer items-center justify-between px-5 heading-sm leading-none outline-none'
            )}
            onClick={() => setIsOpen(!isOpen)}
          >
            {header}
            <ChevronDownIcon
              className={clsx(
                'ease-in-out-expo transition-transform duration-300'
              )}
              style={{ ...(!isOpen ? { transform: 'rotate(270deg)' } : {}) }}
              aria-hidden
            />
          </div>
        </div>
        <animated.div
          className='body'
          style={{ ...contentStyles, ...{ overflowAnchor: 'none' } }}
        >
          {children}
        </animated.div>
      </div>
    </div>
  )
}

export const GustoInstructions = ({
  logoUrl,
  instructionalImages,
}: InstructionsProps) => {
  return (
    <div>
      <img src={logoUrl} className='w-40' />
      <div className='mt-6 border-2 border-gray-200 rounded-lg'>
        <AnimatedAccordionItem header='Benefits Setup (One Time)'>
          <ol className='list-decimal pt-6 ml-6'>
            <li className='py-2 font-medium'>
              Set up the pre-tax health benefit
            </li>
            <ul className='list-disc'>
              <li className='py-2'>
                Navigate to <a href='https://app.gusto.com/payroll_admin/benefits/health/new' target='_blank' rel='noreferrer'>Create a new Health Benefit</a> (Benefit &gt; Medical,
                Dental & Vision Managed by Third Party)
              </li>
              <li className='py-2'>
                Add the benefit name “Thatch Medical Pre Tax“. Select “Other”
                for Carrier and write “Thatch” for Carrier name. Set employee
                deduction to $0 and company contribution to $0.01. Gusto
                requires a nonzero amount to create any benefit. Select Save &
                Continue.
              </li>
              <img
                src={instructionalImages.gusto_one_time_1}
                className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg w-11/12'
              />
              <li className='py-2'>
                Select the employees you would like enrolled and click “Done”
              </li>
              <li className='py-2'>Select “Back to Benefits”</li>
              <img
                src={instructionalImages.gusto_one_time_2}
                className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg w-11/12'
              />
            </ul>
            <li className='py-2 font-medium'>
              Set up the post-tax benefit
            </li>
            <ul className='list-disc'>
              <li className='py-2'>
                Navigate to <a href='https://app.gusto.com/payroll_admin/benefits/custom_benefit/new' target='_blank' rel='noreferrer'>Create a new Custom Benefit</a> (Benefits &gt; Custom
                Benefit)
              </li>
              <li className='py-2'>Name the benefit “Thatch Post Tax”</li>
              <img
                src={instructionalImages.gusto_one_time_3}
                className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg w-11/12'
              />
              <li className='py-2'>
                Add your employees to the benefit and select “Done”
              </li>
            </ul>
          </ol>
        </AnimatedAccordionItem>
        <AnimatedAccordionItem header='Entering Deductions' isLastItem>
          <ol className='list-decimal pt-6 ml-6'>
            <ul className='list-disc'>
              <li className='py-2'>Navigate to your benefit</li>
              <li className='py-2'>
                Select “Edit benefit” for the applicable employee
              </li>
              <img
                src={instructionalImages.gusto_deductions_1}
                className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg w-11/12'
              />
              <li className='py-2'>
                Enter the employee deduction amount. Your payroll file lists the
                monthly amount, so ensure you are entering the amount correct
                for your pay periods.
              </li>
              <li className='py-2'>Set company contribution to $0</li>
              <img
                src={instructionalImages.gusto_deductions_2}
                className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg w-11/12'
              />
              <li className='py-2'>Select “Save”</li>
              <li className='py-2'>Repeat for each employee</li>
            </ul>
          </ol>
        </AnimatedAccordionItem>
      </div>
    </div>
  )
}

export const RipplingInstructions = ({
  logoUrl,
  instructionalImages,
}: InstructionsProps) => {
  return (
    <div>
      <img src={logoUrl} className='w-40' />
      <div className='mt-6 border-2 border-gray-200 rounded-lg'>
        <AnimatedAccordionItem header='Benefits Setup (One Time)'>
          <ol className='list-decimal pt-6 ml-6'>
            <ul className='list-disc'>
              <li className='py-2'>Navigate to Payroll &gt; Deductions</li>
              <li className='py-2'>Click “Create new deduction“</li>
              <li className='py-2'>Select “Medical (Pre-tax)“ and name the benefit “Thatch Medical Pre Tax“</li>
              <img
                src={instructionalImages.rippling_one_time_1}
                className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg w-11/12'
              />
              <li className='py-2'>Select “Input monthly amounts“</li>
              <li className='py-2'>
                Select “No“ for default employee deduction and “No“ for default
                company contribution
              </li>
              <li className='py-2'>Select “No“ for default annual limits</li>
              <li className='py-2'>
                Click “Done“ to complete the benefit setup.
              </li>
              <li className='py-2'>
                Then, repeat the above steps for a Post Tax Deduction with the
                name “Thatch Post Tax“
              </li>
              <img
                src={instructionalImages.rippling_one_time_2}
                className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg w-11/12'
              />
            </ul>
          </ol>
        </AnimatedAccordionItem>
        <AnimatedAccordionItem header='Entering Deductions' isLastItem>
          <ol className='list-decimal pt-6 ml-6'>
            <ul className='list-disc'>
              <li className='py-2'>
                Navigate to Payroll &gt; Deductions &gt; Recurring deductions
                for active employees
              </li>
              <img
                src={instructionalImages.rippling_deductions_1}
                className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
              />
              <li className='py-2'>
                To enter deductions individually, update the “Emp. Deduction”
                field for each employee with the correct per pay period amount.
              </li>
              <img
                src={instructionalImages.rippling_deductions_2}
                className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
              />
              <li className='py-2'>
                To bulk upload deductions, select “Import CSV“
              </li>
              <img
                src={instructionalImages.rippling_deductions_3}
                className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
              />
              <li className='py-2'>
                Upload your CSV from Thatch and select Next. Ensure that you are
                using per pay period amounts.
              </li>
              <li className='py-2'>
                Map your column names to the columns in the CSV then select
                Submit.
              </li>
              <img
                src={instructionalImages.rippling_deductions_4}
                className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
              />
              <li className='py-2'>
                Review the mapping of the employees in the CSV to your Rippling
                employee entries.
              </li>
              <img
                src={instructionalImages.rippling_deductions_5}
                className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
              />
              <li className='py-2'>Select Submit, and then you&apos;re done</li>
            </ul>
          </ol>
        </AnimatedAccordionItem>
      </div>
    </div>
  )
}

export const QuickbooksInstructions = ({
  logoUrl,
  instructionalImages,
}: InstructionsProps) => {
  return (
    <div>
      <img src={logoUrl} className='w-40' />
      <div className='mt-6 border-2 border-gray-200 rounded-lg'>
        <AnimatedAccordionItem header='Add Pre-tax Deduction'>
          <ul className='list-disc pt-6 ml-6'>
            <li className='py-2'>
              Navigate to your employees listing. Click on the employee row.
            </li>
            <img
              src={instructionalImages.quickbooks_deductions_1}
              className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
            />
            <li className='py-2'>
              Scroll to the bottom of the detail page and select “Deductions and
              Contributions”
            </li>
            <img
              src={instructionalImages.quickbooks_deductions_2}
              className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
            />
            <li className='py-2'>Click “Add deduction/contribution”</li>
            <img
              src={instructionalImages.quickbooks_deductions_3}
              className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
            />
            <li className='py-2'>
              Select “Health insurance” and “Medical Insurance”. For description
              use “Thatch Medical Pre Tax”. Set the amount per paycheck and
              leave the employer contribution to ”None”.
            </li>
            <img
              src={instructionalImages.quickbooks_pretax_1}
              className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
            />
            <li className='py-2'>
              Click “Save” and then click “Done”. Repeat for each employee.
            </li>
          </ul>
        </AnimatedAccordionItem>
        <AnimatedAccordionItem header='Add Post-tax Deduction' isLastItem>
          <ul className='list-disc pt-6 ml-6'>
            <li className='py-2'>
              Navigate to your employees listing. Click on the employee row.
            </li>
            <img
              src={instructionalImages.quickbooks_deductions_1}
              className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
            />
            <li className='py-2'>
              Scroll to the bottom of the detail page and select “Deductions and
              Contributions”
            </li>
            <img
              src={instructionalImages.quickbooks_deductions_2}
              className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
            />
            <li className='py-2'>Click “Add deduction/contribution”</li>
            <img
              src={instructionalImages.quickbooks_deductions_3}
              className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
            />
            <li className='py-2'>
              Select “Other deductions” and “Other after tax deductions”. Set the description to “Thatch Post Tax”. Add the per paycheck amount.
            </li>
            <img
              src={instructionalImages.quickbooks_posttax_1}
              className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
            />
            <li className='py-2'>
              Click “Save” and then click “Done”. Repeat for each employee.
            </li>
          </ul>
        </AnimatedAccordionItem>
      </div>
    </div>
  )
}

export const ADPRunInstructions = ({
  logoUrl,
  instructionalImages,
}: InstructionsProps) => {
  return (
    <div>
      <img src={logoUrl} className='w-40' />
      <div className='mt-6 border-2 border-gray-200 rounded-lg'>
        <AnimatedAccordionItem header='Benefits Setup (One Time)'>
          <ul className='list-disc pt-6 ml-6'>
            <li className='py-2'>Click “Settings” on the left hand sidebar</li>
            <img
              src={instructionalImages.adprun_one_time_1}
              className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
            />
            <li className='py-2'>
              Under the Payroll section, click “Earnings and deductions”
            </li>
            <img
              src={instructionalImages.adprun_one_time_2}
              className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
            />
            <li className='py-2'>Click “Add Deduction” at the bottom</li>
            <img
              src={instructionalImages.adprun_one_time_3}
              className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
            />
            <li className='py-2'>
              Select “Pre-tax insurances” and “Medical pre-tax 1”. Rename the
              deduction to “Thatch Medical Pre Tax”
            </li>
            <img
              src={instructionalImages.adprun_one_time_4}
              className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
            />
            <li className='py-2'>
              Note: Ensure that your deductions match your pay period schedule.
              If you would like the deduction to apply only to the first and
              second paycheck of the month, select “Schedule Deductions” and
              then choose “First” and “Second”. Otherwise leave this setting
              alone.
            </li>
            <img
              src={instructionalImages.adprun_one_time_5}
              className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
            />
            <li className='py-2'>Click “Save”</li>
            <li className='py-2'>
              Set up the post tax deduction using the same steps. Select
              “Miscellaneous deductions” and any one of the “Miscellaneous”
              amounts. Set the name to “Thatch Post Tax”
            </li>
            <img
              src={instructionalImages.adprun_one_time_6}
              className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
            />
          </ul>
        </AnimatedAccordionItem>
        <AnimatedAccordionItem header='Enter Deductions' isLastItem>
          <ul className='list-disc pt-6 ml-6'>
            <li className='py-2'>
              Deductions are entered on an individual basis. Select “People” in
              the left hand sidebar and click on the employee you&apos;d like to
              add the deduction to.
            </li>
            <img
              src={instructionalImages.adprun_deductions_1}
              className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
            />
            <li className='py-2'>
              Click “Earnings Deductions” on the left hand sidebar and then
              under Other deductions choose “Add deduction”
            </li>
            <img
              src={instructionalImages.adprun_deductions_2}
              className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
            />
            <li className='py-2'>
              Choose which deduction, and then set the per-paycheck deduction
              amount for the employee
            </li>
            <img
              src={instructionalImages.adprun_deductions_3}
              className='my-2 h-full shadow ring-1 ring-black ring-opacity-5 md:rounded-lg max-w-xl w-11/12'
            />
            <li className='py-2'>Hit “Save” to finish</li>
          </ul>
        </AnimatedAccordionItem>
      </div>
    </div>
  )
}

export const OtherInstructions = () => {
  return (
    <div className='body'>
      <div className='pt-4'>
        If your payroll provider is not listed, please reach out to our <a href='mailto:support@thatch.com'>payroll support team</a> and we would be happy to assist you in setting up your deductions.
      </div>
    </div>
  )
}
